// react
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// framer
import { m } from 'framer-motion';
// @mui
import { styled, keyframes } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Stack, { StackProps } from '@mui/material/Stack';
// routes
import { PATH } from '../../routes/paths';
// components
import Iconify from '../../components/Iconify';
import { Lottie } from '../../components/Lottie';
import { MotionContainer, varFade } from '../../components/animate';
// hooks
import useResponsive from '../../hooks/useResponsive';

// ----------------------------------------------------------------------

const RootStyle = styled(m.div)(({ theme }) => ({
  position: 'relative',
  backgroundColor: theme.palette.grey[400],
  height: '100vh',
  [theme.breakpoints.up('md')]: {
    top: 0,
    left: 0,
    width: '100%',

    display: 'flex',
    position: 'fixed',
    alignItems: 'center',
  },
}));

const ContentStyle = styled((props: StackProps) => <Stack spacing={5} {...props} />)(
  ({ theme }) => ({
    zIndex: 10,
    maxWidth: 520,
    margin: 'auto',
    textAlign: 'center',
    position: 'relative',
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(15),
    [theme.breakpoints.up('md')]: {
      margin: 'unset',
      textAlign: 'left',
    },
  })
);

const HeroOverlayStyle = styled(m.img)({
  zIndex: 9,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  position: 'absolute',
});

// const HeroImgStyle = styled(m.img)(({ theme }) => ({
//   top: 0,
//   right: 0,
//   bottom: 0,
//   zIndex: 8,
//   width: '100%',
//   margin: 'auto',
//   position: 'absolute',
//   [theme.breakpoints.up('lg')]: {
//     right: '8%',
//     width: 'auto',
//     height: '60vh',
//   },
// }));

const HeroDivStyle = styled('div')(({ theme }) => ({
  top: 0,
  right: 0,
  bottom: 0,
  zIndex: 8,
  width: '100%',
  margin: 'auto',
  position: 'absolute',
  [theme.breakpoints.up('lg')]: {
    right: '8%',
    width: 'auto',
    height: '70vh',
    opacity: 0.9,
    zIndex: 10,
  },
}));

const spin = keyframes`
  0%, 100% {
    color: #00AB55;
  }
  75% {
    color: #C8FACD;
  }
`;

const IconAnimated = styled(Iconify)({
  animation: `${spin} 5s infinite`,
  '&:hover': {
    transform: 'scale(1.1)',
  },
});

// ----------------------------------------------------------------------

export default function HomeHero() {
  const isSmall = useResponsive('down', 'md');
  const [animationData, setAnimationData] = useState<any>(null);

  useEffect(() => {
    fetch('/assets/home/company.lottie.json', {
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }).then((res) => res.json().then((data) => setAnimationData(data)));
  }, []);

  return (
    <MotionContainer>
      <RootStyle>
        <HeroOverlayStyle alt="overlay" src="/assets/overlay.svg" variants={varFade().in} />

        {/* <HeroImgStyle alt="hero" src="/assets/home/company.svg" variants={varFade().inUp} /> */}
        {animationData !== null && (
          <HeroDivStyle>
            <Lottie animationData={animationData} width="100%" height="100%" />
          </HeroDivStyle>
        )}

        <Container>
          <ContentStyle>
            <m.div variants={varFade().inRight}>
              <Typography variant="h1" sx={{ color: 'common.white' }}>
                Join the <br />
                data revolution <br /> with
                <Typography component="span" variant="h1" sx={{ color: 'primary.main' }}>
                  &nbsp;dratio.io
                </Typography>
              </Typography>
            </m.div>

            <m.div variants={varFade().inRight}>
              <Typography sx={{ color: 'common.white' }}>
                The bridge between data and knowledge has never been shorter <br />
                {!isSmall && 'We help you to improve decision making through technology'}
              </Typography>
            </m.div>

            <m.div variants={varFade().inRight}>
              {!isSmall ? (
                <Button
                  size="large"
                  variant="contained"
                  component={RouterLink}
                  to={PATH.getStarted}
                >
                  Get Started
                </Button>
              ) : (
                <Stack alignItems="center">
                  <IconAnimated
                    icon="eva:arrowhead-down-fill"
                    width={50}
                    height={50}
                    onClick={() => {
                      const divElement = document.getElementById('dratio-home-minimal');
                      if (divElement) {
                        divElement.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                  />
                </Stack>
              )}
            </m.div>
          </ContentStyle>
        </Container>
      </RootStyle>
      <Box sx={{ height: { md: '100vh' } }} />
    </MotionContainer>
  );
}
